import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRightIcon, TruckIcon, ArrowPathIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { productsAPI } from "../services/api";
import ProductCard from "../components/ui/ProductCard";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import LiveChat from "../components/support/LiveChat";

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
  const [newArrivals, setNewArrivals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featRes, newRes] = await Promise.all([
          productsAPI.list({ is_featured: true, page_size: 4 }),
          productsAPI.list({ sort_by: "created_at", sort_order: "desc", page_size: 8 }),
        ]);
        setFeaturedProducts(featRes.data.items || []);
        setNewArrivals(newRes.data.items || []);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Kushi Fashions – Elegant Dresses for Every Occasion</title>
        <meta name="description" content="Shop the latest collection of dresses at Kushi Fashions. Kurtas, sarees, western wear & more. Free shipping above ₹999." />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-50 via-pink-50 to-rose-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-20 sm:py-32 flex flex-col md:flex-row items-center gap-10">
          <div className="flex-1 text-center md:text-left">
            <span className="badge bg-primary-100 text-primary-700 mb-4">New Collection {new Date().getFullYear()}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Dress to
              <span className="text-primary-600 block">Impress ✨</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto md:mx-0">
              Discover handpicked dresses for every occasion. From elegant kurtas to stunning western wear.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <Link to="/products" className="btn-primary py-3 px-8 flex items-center justify-center gap-2">
                Shop Now <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <Link to="/products?is_featured=true" className="btn-secondary py-3 px-8">
                New Arrivals
              </Link>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative w-72 h-80 sm:w-96 sm:h-[28rem]">
              <div className="absolute top-0 right-0 w-64 h-80 sm:w-80 sm:h-96 bg-primary-200 rounded-3xl rotate-6 opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-br from-primary-300 to-pink-300 rounded-3xl -rotate-3" />
              <div className="absolute inset-4 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=600"
                  alt="Fashion Hero"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: TruckIcon, title: "Free Shipping", desc: "On orders above ₹999" },
              { icon: ArrowPathIcon, title: "Easy Returns", desc: "7-day return policy" },
              { icon: ShieldCheckIcon, title: "Secure Payment", desc: "100% secure with Razorpay" },
            ].map((feature) => (
              <div key={feature.title} className="flex items-center gap-4 p-5 rounded-xl bg-primary-50">
                <feature.icon className="h-10 w-10 text-primary-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800">{feature.title}</p>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Shop by Category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { name: "Kurtas", category: "kurta", emoji: "👗", color: "bg-orange-50 text-orange-600" },
            { name: "Sarees", category: "saree", emoji: "🥻", color: "bg-red-50 text-red-600" },
            { name: "Western", category: "western", emoji: "👚", color: "bg-blue-50 text-blue-600" },
            { name: "Ethnic", category: "ethnic", emoji: "💃", color: "bg-purple-50 text-purple-600" },
          ].map((cat) => (
            <Link
              key={cat.category}
              to={`/products?category=${cat.category}`}
              className={`${cat.color} rounded-2xl p-6 text-center hover:shadow-md transition-shadow group`}
            >
              <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">{cat.emoji}</div>
              <p className="font-semibold">{cat.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      {!loading && featuredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Featured Picks</h2>
            <Link to="/products?is_featured=true" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View all <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* New Arrivals */}
      {loading ? (
        <LoadingSpinner className="py-20" size="lg" />
      ) : (
        newArrivals.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 pb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">New Arrivals</h2>
              <Link to="/products" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
                View all <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {newArrivals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )
      )}

      {/* CTA Banner */}
      <section className="bg-primary-600 text-white py-16">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold mb-4">Get ₹200 Off Your First Order!</h2>
          <p className="text-primary-100 mb-6">Use code <strong>KUSHI200</strong> at checkout. Min order ₹999.</p>
          <Link to="/register" className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-full hover:bg-primary-50 transition-colors">
            Create Account
          </Link>
        </div>
      </section>

      <LiveChat />
    </>
  );
}
